import { useQuery } from '@apollo/client';
import type { Pills } from '@seek/chalice-types';
import { useHubble } from '@seek/hubble';
import { useResponsiveValue } from 'braid-design-system';
import type React from 'react';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';

import {
  GetJobSearchAssistDocument,
  type JobSearchAssistPills,
} from 'src/graphql/graphql';
import { logger } from 'src/modules/logger';
import { useFeatureFlagRefineBarV2Experiment } from 'src/store/featureFlags/hooks';
import { useDispatch, useSelector } from 'src/store/react';
import {
  clearJobSearchAssistState,
  updateBaseKeywordParam,
  updateJobSearchAssistState,
  updateKeywordsField,
} from 'src/store/search';
import { selectBaseKeywords } from 'src/store/search/selectors';
import { useRunSearch } from 'src/store/search/useRunSearch';
import {
  selectAsyncPillsToken,
  selectFeatureFlag,
  selectIsHomePage,
  selectIsResultsLoading,
  selectPills,
  selectSolId,
} from 'src/store/selectors';

export type JobSearchAssist = {
  isJobSearchAssistEnabled: boolean;
  isShowingScrollButton: boolean;
  isShowingTooltip: boolean;
  isLoading: boolean;
  pills: Pills | JobSearchAssistPills['data'];
  resetState: ({ keywords }: { keywords?: string }) => void;
  onPillClick: ({
    keywords,
    stateToken,
  }: {
    keywords: string;
    stateToken?: string;
  }) => void;
};

// Create context with a default value
const JobSearchAssistContext = createContext<JobSearchAssist>({
  isJobSearchAssistEnabled: false,
  isShowingScrollButton: false,
  isShowingTooltip: false,
  isLoading: false,
  pills: [],
  onPillClick: () => {},
  resetState: () => {},
});

// Provider component that wraps parts of your app that need JobSearchAssist
export const JobSearchAssistProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const dispatch = useDispatch();

  const _isJobSearchAssistEnabled = useSelector(
    selectFeatureFlag('isJobSearchAssistEnabled'),
  );
  const runSearch = useRunSearch();

  /**
   * Visitor ID and Token Management Strategy:
   *
   * - serverVisitorId: Persisted from server-side rendering (SSR)
   * - clientVisitorId: Generated dynamically on the client-side
   * - asyncPillsToken: Associated with serverVisitorId during SSR
   *
   * We must use serverVisitorId for the initial request to maintain token validity,
   * then transition to clientVisitorId for all subsequent requests on the client-side to ensure
   * proper job assist functionality.
   */

  const serverVisitorId = useSelector(selectSolId);
  const clientVisitorId = useHubble().visitorId();
  // Ensure that the clientVisitorId is set only once when the component mounts
  useEffect(() => {
    currentVisitorId.current = clientVisitorId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize the tokens array to prevent unnecessary re-renders
  const asyncPillsToken = useSelector(selectAsyncPillsToken);
  const tokens = useMemo(() => [asyncPillsToken], [asyncPillsToken]);

  const currentVisitorId = useRef(serverVisitorId);

  const baseKeywords = useSelector(selectBaseKeywords);
  const isHomePage = useSelector(selectIsHomePage);
  const isLoading = useSelector(selectIsResultsLoading);
  const pills = useSelector(selectPills);

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false,
  });

  const { refineBarV2, dynamicPillsV2 } = useFeatureFlagRefineBarV2Experiment();
  const isJobSearchAssistEnabled =
    _isJobSearchAssistEnabled &&
    asyncPillsToken &&
    (refineBarV2 || dynamicPillsV2);

  const queryOptions = useMemo(
    () => ({
      variables: {
        tokens,
        visitorId: currentVisitorId.current,
      },
      context: {
        headers: {
          'x-custom-features': 'application/features.seek.all+json',
        },
      },
      fetchPolicy: 'cache-and-network' as const,
      errorPolicy: 'all' as const,
      skip: !isJobSearchAssistEnabled,
      ssr: false,
    }),
    [tokens, isJobSearchAssistEnabled],
  );

  // Execute the query with the memoized options
  const {
    loading: isJobSearchAssistLoading,
    data,
    error,
  } = useQuery(GetJobSearchAssistDocument, queryOptions);

  if (error) {
    logger.error(
      { err: error, input: queryOptions.variables },
      'JobSearchAssist GraphQL Query Error',
    );
  }

  // Memoize callbacks to maintain stable references
  const resetStateToken = useRef(() => {
    dispatch(clearJobSearchAssistState());
  });

  const onJobSearchAssistPillClick = useRef(
    ({ keywords, stateToken }: { keywords: string; stateToken?: string }) => {
      if (stateToken) {
        dispatch(
          updateJobSearchAssistState({
            keywords,
            stateToken,
          }),
        );
      }
      dispatch(updateKeywordsField(keywords));
      dispatch(runSearch({ pathname: '/jobs' }));
    },
  );

  const resetBaseKeywordParam = useRef(
    ({ keywords = '' }: { keywords?: string } = {}) => {
      dispatch(updateBaseKeywordParam(keywords));
    },
  );

  const onPillClick = useMemo(
    () =>
      ({ keywords }: { keywords: string }) => {
        if (!baseKeywords) {
          dispatch(updateBaseKeywordParam(keywords));
        }
        dispatch(updateKeywordsField(keywords));
        dispatch(runSearch({ pathname: '/jobs' }));
      },
    [baseKeywords, dispatch, runSearch],
  );

  const jobSearchAssistPills = useMemo(
    () => data?.jobSearchAssist?.pills?.data || [],
    [data?.jobSearchAssist?.pills?.data],
  );

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () =>
      isJobSearchAssistEnabled
        ? {
            isJobSearchAssistEnabled: true,
            isShowingScrollButton:
              !isMobile &&
              !isHomePage &&
              !isJobSearchAssistLoading &&
              !isLoading,
            isShowingTooltip: Boolean(
              !isHomePage &&
                (dynamicPillsV2 || refineBarV2) &&
                !isJobSearchAssistLoading &&
                !isLoading &&
                jobSearchAssistPills.length > 0,
            ),
            isLoading: isJobSearchAssistLoading || isLoading,
            pills: jobSearchAssistPills,
            resetState: resetStateToken.current,
            onPillClick: onJobSearchAssistPillClick.current,
          }
        : {
            isJobSearchAssistEnabled: false,
            isShowingScrollButton: !isMobile && !isHomePage,
            isShowingTooltip: Boolean(
              !isLoading &&
                (dynamicPillsV2 || refineBarV2) &&
                pills &&
                pills?.length > 0,
            ),
            isLoading,
            pills: pills || [],
            resetState: resetBaseKeywordParam.current,
            onPillClick,
          },
    [
      isJobSearchAssistEnabled,
      isMobile,
      isHomePage,
      dynamicPillsV2,
      refineBarV2,
      isJobSearchAssistLoading,
      isLoading,
      jobSearchAssistPills,
      pills,
      onPillClick,
    ],
  );

  return (
    <JobSearchAssistContext.Provider value={contextValue}>
      {children}
    </JobSearchAssistContext.Provider>
  );
};

export const useJobSearchAssist = (): JobSearchAssist => {
  const context = useContext(JobSearchAssistContext);

  return context;
};

export default useJobSearchAssist;
